<script setup>
import { route } from 'ziggy-js';
import { Head, Link, router, useForm } from '@inertiajs/vue3';
import { onMounted, ref } from 'vue';
import AuthenticationCard from '@/components/AuthenticationCard.vue';
import Alert from '@/components/ui/alert/Alert.vue';
import Checkbox from '@/components/ui/checkbox/Checkbox.vue';
import InputError from '@/components/ui/input/InputError.vue';
import Label from '@/components/ui/input/Label.vue';
import Input from '@/components/ui/input/Input.vue';
import AppLogo from '@/vue/components/dashboard/atoms/AppLogo.vue';
import Button from '@/components/ui/button/Button.vue';

defineProps({
    canResetPassword: { type: Boolean, default: false },
    status: { type: String, default: '' },
    canQuickLogin: { type: Boolean, default: false },
});

const sessionExpired = ref(false);

onMounted(() => {
    if (sessionStorage.getItem('session_expired')) {
        sessionExpired.value = true;
        sessionStorage.removeItem('session_expired');
    }
});

const form = useForm({
    email: '',
    password: '',
    remember: false,
});

const submit = () => {
    form.transform((data) => ({
        ...data,
        remember: form.remember ? 'on' : '',
    })).post(route('login'), {
        onFinish: () => form.reset('password'),
    });
};

const quickLoginProcessing = ref(false);

const quickLogin = (userType) => {
    quickLoginProcessing.value = true;
    router.post(
        route(`dev-login.${userType}`),
        {},
        {
            onFinish: () => {
                quickLoginProcessing.value = false;
            },
        }
    );
};
</script>

<template>
    <Head>
        <title>Log in</title>
        <link rel="icon" type="image/x-icon" href="/images/assets/logos/grand-logo-icon.png" />
        <link rel="icon" type="image/svg+xml" href="/images/assets/logos/grand-logo-icon.png" />
        <link rel="icon" type="image/svg+xml" href="/images/assets/logos/grand-logo-icon.png" />
        <link rel="apple-touch-icon" type="image/x-icon" href="/images/assets/logos/grand-logo-icon.png" />
    </Head>

    <AuthenticationCard>
        <template #logo>
            <AppLogo class="w-44 md:w-56 h-auto my-4" />
        </template>

        <Alert
            v-if="sessionExpired"
            variant="warning"
            class="mb-4"
            :description="$t('Your session has expired. Please log in again.')"
        />

        <div v-if="status" class="mb-4 font-medium text-sm text-green-600">
            {{ status }}
        </div>

        <form @submit.prevent="submit" class="space-y-4">
            <div>
                <Label for="email" class="text-gray-900">Email</Label>
                <Input
                    variation="alt"
                    id="email"
                    v-model="form.email"
                    type="email"
                    class="block w-full"
                    required
                    autofocus
                    autocomplete="username"
                    data-testid="email-input"
                />
                <InputError class="mt-2" :message="form.errors.email" />
            </div>

            <div>
                <Label for="password" class="text-gray-900">Password</Label>
                <Input
                    variation="alt"
                    id="password"
                    v-model="form.password"
                    type="password"
                    class="block w-full"
                    required
                    autocomplete="current-password"
                    data-testid="password-input"
                />
                <InputError class="mt-2" :message="form.errors.password" />
            </div>

            <div class="block">
                <label class="flex items-center">
                    <Checkbox v-model:checked="form.remember" name="remember" class="shadow-none" />
                    <span class="ms-2 text-sm text-stone-500">Remember me</span>
                </label>
            </div>

            <div class="flex items-end justify-between">
                <Link
                    v-if="canResetPassword"
                    :href="route('password.request')"
                    class="underline underline-offset-4 text-sm text-stone-500 hover:text-gray-900 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand"
                >
                    Forgot your password?
                </Link>

                <Button
                    data-testid="login-button"
                    type="submit"
                    size="lg"
                    :class="{ 'opacity-25': form.processing }"
                    :disabled="form.processing"
                >
                    Log in
                </Button>
            </div>
        </form>

        <div v-if="canQuickLogin" class="mt-6 border-t border-stone-200 pt-4">
            <p class="mb-2 text-center text-xs text-stone-500">Local environment</p>
            <div class="flex gap-2">
                <Button
                    data-testid="dev-login-vendor"
                    variant="outline"
                    class="flex-1"
                    :disabled="quickLoginProcessing"
                    @click="quickLogin('vendor')"
                >
                    Log in as Vendor
                </Button>
                <Button
                    data-testid="dev-login-admin"
                    variant="outline"
                    class="flex-1"
                    :disabled="quickLoginProcessing"
                    @click="quickLogin('admin')"
                >
                    Log in as Admin
                </Button>
            </div>
        </div>
    </AuthenticationCard>
</template>
